import PropTypes from "prop-types";
import * as React from "react";
import { Button } from "react-ui";

import { ICandyApiDraftClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import {
    EXPERT_CREACTE_IN_EXPERT_CLICK,
    EXPERT_GENERATE_ERROR,
    EXPERT_REPLACE_WITH_EXPERT_CLICK,
} from "../../metricsAction";
import { track, trackAction } from "../../Internals/Metrics/MetricsProvider";
import {
    CreateExplanatoryNoteResponse,
    CreateExplanatoryNoteStatus,
} from "../../../../../Common/CandyApiClient/Types/ExplanatoryNote/CreateExplanatoryNoteResponse";
import { ofReactType } from "../../TypingUtils";
import { ErrorLog } from "../../../Engine/Core/ErrorManager/Utils/Models";

import { MessageBox, MessageBoxProps } from "./MessageBox";
import { PrepareNote } from "./PrepareNote";
import { PreviewNote } from "./PreviewNote";
import { ReplaceAttachment } from "./ReplaceAttachment";

import { Name } from "./index";

export interface ExpertNoteProps {
    children: JSX.Element;
    attachment: string | undefined;
    errorLog?: ErrorLog;
    getExpertErrorLog: () => void;
    getExplanatoryNote: () => Promise<CreateExplanatoryNoteResponse>;
    onAddExplanatoryNote: (noteDriveUri: undefined | string) => Promise<void>;
    onReplaceExplanatoryNote: (attachmentInstance: string, noteLink: undefined | string) => Promise<void>;
    candyApiDraftClient: ICandyApiDraftClient;
    hideAttachButton?: boolean;
    isReadOnly?: boolean;
}

interface State {
    modalOpened: boolean;
    replaceView: boolean;
    documentView: boolean;
    error: boolean;
    explanatoryNote: CreateExplanatoryNoteResponse | undefined;
}

const closeState = {
    modalOpened: false,
    replaceView: false,
    documentView: false,
    error: false,
    explanatoryNote: undefined,
};

interface ExpectNoteContextPush {
    handleClose: () => void;
}

export class ExpertNote extends React.PureComponent<ExpertNoteProps, State> {
    public state: State = closeState;
    public static childContextTypes = { handleClose: PropTypes.func };

    public getChildContext(): ExpectNoteContextPush {
        return {
            handleClose: this.handleClose,
        };
    }

    public render(): JSX.Element {
        const children = React.Children.toArray(this.props.children);
        const name = children.find(ofReactType(Name));
        const messageBox = children.find(ofReactType(MessageBox));

        return (
            <span>
                <Button use="link" onClick={this.handleOpen}>
                    {name}
                </Button>
                {this.state.modalOpened ? this.renderModal(messageBox) : undefined}
            </span>
        );
    }

    private readonly handleClose = (): void => {
        this.setState(closeState);
    };

    private readonly handleOpen = (): void => {
        if (this.props.attachment) {
            this.replaceWithExpert();
        } else {
            this.createInExpert();
        }
        this.setState({ modalOpened: true });

        this.props.getExpertErrorLog();
    };

    private readonly handleCreateNote = (): void => {
        this.setState({ documentView: true });

        this.props
            .getExplanatoryNote()
            .then((explanatoryNoteResult: CreateExplanatoryNoteResponse) => {
                if (explanatoryNoteResult.status !== CreateExplanatoryNoteStatus.Success) {
                    track("candy", EXPERT_GENERATE_ERROR, explanatoryNoteResult);
                }
                this.setState({ explanatoryNote: explanatoryNoteResult });
            })
            .catch((e: Error) => {
                // eslint-disable-next-line no-console
                console.error("Ошибка c получением из эксперта", e);
                this.setState({ error: true });
            });
    };

    private readonly handleReloadNote = (): void => {
        this.setState({
            error: false,
            explanatoryNote: undefined,
        });
        this.handleCreateNote();
    };

    private readonly handleDownloadNote = () => {
        const { explanatoryNote } = this.state;
        if (explanatoryNote && explanatoryNote.status === CreateExplanatoryNoteStatus.Success) {
            window.open(explanatoryNote.driveUri);
        }
    };

    private readonly handleAttachNote = (): void => {
        if (this.props.attachment) {
            this.setState({ replaceView: true });
            return;
        }

        // Очень мутная история, надо как-то правильно всё тут сделать
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.addNote();
        this.setState(closeState);
    };

    private readonly handleAttachmentReplace = async () => {
        if (!this.props.attachment) {
            return;
        }

        try {
            const { explanatoryNote } = this.state;
            const noteLink =
                explanatoryNote?.status === CreateExplanatoryNoteStatus.Success ? explanatoryNote.driveUri : undefined;
            await this.props.onReplaceExplanatoryNote(this.props.attachment, noteLink);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("Не удалось заменить файл ExpertNote", e);
        }
        this.setState(closeState);
    };

    private readonly goToPreviewPage = () => {
        this.setState({ documentView: true, replaceView: false });
    };

    private renderModal(messageBox?: React.ReactElement<MessageBoxProps>): JSX.Element {
        if (this.state.replaceView && !this.props.hideAttachButton) {
            return (
                <ReplaceAttachment
                    onClose={this.handleClose}
                    onAttachmentReplace={this.handleAttachmentReplace}
                    onGoToPreviewPage={this.goToPreviewPage}
                    onDownloadNote={this.handleDownloadNote}
                />
            );
        }
        if (this.state.documentView) {
            return (
                <PreviewNote
                    explanatoryNote={this.state.explanatoryNote}
                    error={this.state.error}
                    handleClose={this.handleClose}
                    handleAttachNote={this.handleAttachNote}
                    handleDownloadNote={this.handleDownloadNote}
                    handleReloadNote={this.handleReloadNote}
                    hideAttachButton={this.props.hideAttachButton ?? false}
                    isReadOnly={this.props.isReadOnly}
                />
            );
        }

        return (
            <PrepareNote
                candyApiDraftClient={this.props.candyApiDraftClient}
                handleClose={this.handleClose}
                messageBox={messageBox}
                handleCreateNote={this.handleCreateNote}
                errorLog={this.props.errorLog}
                getExpertErrorLog={() => this.props.getExpertErrorLog()}
            />
        );
    }

    private async addNote(): Promise<void> {
        const { explanatoryNote } = this.state;
        const noteLink =
            explanatoryNote?.status === CreateExplanatoryNoteStatus.Success ? explanatoryNote.driveUri : undefined;
        await this.props.onAddExplanatoryNote(noteLink);
    }

    @trackAction(EXPERT_CREACTE_IN_EXPERT_CLICK)
    private readonly createInExpert = (): void => {
        /* Metric */
    };

    @trackAction(EXPERT_REPLACE_WITH_EXPERT_CLICK)
    private readonly replaceWithExpert = (): void => {
        /* Metric */
    };
}
